// ABOUTME: Modal component for admin users to reset passwords for other users
// ABOUTME: Provides secure password reset functionality with validation and audit logging

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import axios from 'axios';
import { adminAPI } from '../services/api';

interface AdminPasswordResetModalProps {
  open: boolean;
  user: {
    userId: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  onClose: () => void;
  onSuccess: (message: string) => void;
}

interface FormData {
  newPassword: string;
  confirmPassword: string;
}

const AdminPasswordResetModal: React.FC<AdminPasswordResetModalProps> = ({
  open,
  user,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<FormData>({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({
    newPassword: false,
    confirmPassword: false
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setFormData({
        newPassword: '',
        confirmPassword: ''
      });
      setError(null);
      setShowPassword({
        newPassword: false,
        confirmPassword: false
      });
      setTouched({
        newPassword: false,
        confirmPassword: false
      });
      
      // Auto-focus the first password field
      setTimeout(() => {
        const firstField = document.querySelector('[data-testid="new-password-input"]') as HTMLInputElement;
        if (firstField) {
          firstField.focus();
        }
      }, 100);
    }
  }, [open]);

  const validateForm = (): string | null => {
    if (formData.newPassword.length < 8 || formData.newPassword.length > 64) {
      return 'Password must be between 8 and 64 characters';
    }
    if (formData.newPassword !== formData.confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const isFormValid = (): boolean => {
    return formData.newPassword.length >= 8 && 
           formData.newPassword.length <= 64 &&
           formData.newPassword === formData.confirmPassword;
  };

  const getPasswordValidationError = (): string | null => {
    if (!touched.newPassword || formData.newPassword.length === 0) return null;
    if (formData.newPassword.length < 8) return 'Password must be at least 8 characters';
    if (formData.newPassword.length > 64) return 'Password must be 64 characters or less';
    return null;
  };

  const getConfirmPasswordError = (): string | null => {
    if (!touched.confirmPassword || formData.confirmPassword.length === 0) return null;
    if (formData.newPassword !== formData.confirmPassword) return 'Passwords do not match';
    return null;
  };

  const handleInputChange = (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleBlur = (field: keyof FormData) => () => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const togglePasswordVisibility = (field: keyof typeof showPassword) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await adminAPI.resetUserPassword(user.userId, {
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      });

      onSuccess('Password reset successfully');
      onClose();
    } catch (err: unknown) {
      // Map specific API errors to user-friendly messages
      const responseMessage = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
        : undefined;
      if (responseMessage) {
        setError(responseMessage);
      } else if (err instanceof Error && err.message.includes('OAuth')) {
        setError('Cannot reset password for OAuth users');
      } else if (err instanceof Error && err.message.includes('Password')) {
        setError(err.message);
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing via backdrop click or escape key during loading
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          if (!loading) {
            onClose();
          }
          return;
        }
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      data-testid="password-reset-modal"
    >
      <DialogTitle>
        Reset Password for {user.firstName} {user.lastName}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            type={showPassword.newPassword ? 'text' : 'password'}
            label="New Password"
            value={formData.newPassword}
            onChange={handleInputChange('newPassword')}
            onBlur={handleBlur('newPassword')}
            disabled={loading}
            error={!!getPasswordValidationError()}
            helperText={getPasswordValidationError() || "Must be between 8 and 64 characters"}
            margin="normal"
            data-testid="new-password-input"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility('newPassword')}
                    edge="end"
                    aria-label="toggle password visibility"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword.newPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <TextField
            fullWidth
            type={showPassword.confirmPassword ? 'text' : 'password'}
            label="Confirm New Password"
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            onBlur={handleBlur('confirmPassword')}
            disabled={loading}
            error={!!getConfirmPasswordError()}
            helperText={getConfirmPasswordError() || "Re-enter the password to confirm"}
            margin="normal"
            data-testid="confirm-password-input"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility('confirmPassword')}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!isFormValid() || loading}
          variant="contained"
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { AdminPasswordResetModal };